/**
 * Campo único de busca com lista (combobox): clicar mostra as opções, digitar
 * filtra na hora (sem acento, sem diferença de maiúscula), setas e Enter
 * escolhem, Esc fecha. Com `buscarRemoto`, a partir de 3 letras também busca no
 * servidor (ex.: todo o cadastro de prestadores por nome, código ou CNPJ).
 *
 * Substitui o par "select + caixa de filtro" da Rede e preços, que obrigava a
 * digitar num lugar e abrir a lista em outro.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";

const C = { ink: "#2c3e50", soft: "#7f8c8d", line: "#e8e8e8", teal: "#20a490", tealSoft: "#e8f6f4" };
const norm = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
const soDigitos = (t) => String(t || "").replace(/\D/g, "");

export default function CampoBusca({
  rotulo, ajuda, valor, rotuloValor, opcoes = [], buscarRemoto, onEscolher, onLimpar,
  placeholder = "Digite para buscar", vazio = "Nada encontrado", largura = 300, ariaLabel,
}) {
  const [aberto, setAberto] = useState(false);
  const [q, setQ] = useState("");
  const [remotas, setRemotas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const caixa = useRef(null);
  const lista = useRef(null);

  // fecha ao clicar fora
  useEffect(() => {
    const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) { setAberto(false); setQ(""); } };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  // busca no servidor a partir de 3 letras (com pausa curta entre teclas)
  useEffect(() => {
    if (!buscarRemoto || q.trim().length < 3) { setRemotas([]); setBuscando(false); return undefined; }
    setBuscando(true);
    let vivo = true;
    const h = setTimeout(() => {
      buscarRemoto(q.trim()).then((r) => vivo && setRemotas(r || [])).catch(() => vivo && setRemotas([]))
        .finally(() => vivo && setBuscando(false));
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q, buscarRemoto]);

  const visiveis = useMemo(() => {
    const t = norm(q.trim()), d = soDigitos(q);
    const casa = (o) => {
      if (!t) return true;
      const alvo = norm(`${o.rotulo} ${o.detalhe || ""}`);
      return t.split(/\s+/).every((p) => alvo.includes(p)) || (d.length >= 3 && soDigitos(o.detalhe).includes(d));
    };
    const locais = opcoes.filter(casa);
    const ja = new Set(locais.map((o) => o.valor));
    return [...locais, ...remotas.filter((o) => !ja.has(o.valor)).map((o) => ({ ...o, grupo: o.grupo || "Busca no cadastro" }))];
  }, [opcoes, remotas, q]);

  useEffect(() => { setAtivo(0); }, [q, aberto]);
  useEffect(() => {
    const el = lista.current?.querySelector(`[data-i="${ativo}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [ativo]);

  const escolher = (o) => { onEscolher(o.valor, o); setAberto(false); setQ(""); };
  const tecla = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAberto(true); setAtivo((i) => Math.min(i + 1, visiveis.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (aberto && visiveis[ativo]) escolher(visiveis[ativo]); else setAberto(true); }
    else if (e.key === "Escape") { setAberto(false); setQ(""); }
  };

  let grupoAnterior = null;
  return (
    <div ref={caixa} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: C.soft, position: "relative", width: largura, maxWidth: "100%" }}>
      {rotulo && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{rotulo}{ajuda}</span>}
      <div style={{ display: "flex", alignItems: "center", border: `1px solid ${aberto ? C.teal : C.line}`, borderRadius: 6, background: "#fff", boxShadow: aberto ? `0 0 0 2px ${C.tealSoft}` : "none" }}>
        <input
          value={aberto ? q : (rotuloValor || "")}
          onChange={(e) => { setQ(e.target.value); setAberto(true); }}
          onFocus={() => setAberto(true)} onClick={() => setAberto(true)} onKeyDown={tecla}
          placeholder={aberto ? (rotuloValor ? `${rotuloValor} (digite para trocar)` : placeholder) : placeholder}
          aria-label={ariaLabel || rotulo} role="combobox" aria-expanded={aberto} aria-autocomplete="list"
          style={{ flex: 1, minWidth: 0, border: 0, outline: "none", fontSize: 12.5, padding: "6px 8px", background: "transparent", color: C.ink, textOverflow: "ellipsis" }} />
        {onLimpar && valor && !aberto && (
          <button type="button" onClick={onLimpar} title="Limpar" aria-label="Limpar" style={{ border: 0, background: "transparent", cursor: "pointer", padding: "0 4px", color: C.soft, display: "flex" }}><X size={14} /></button>
        )}
        <button type="button" onMouseDown={(e) => { e.preventDefault(); setAberto((a) => !a); }} aria-label="Abrir lista" tabIndex={-1}
                style={{ border: 0, background: "transparent", cursor: "pointer", padding: "0 6px", color: C.soft, display: "flex" }}><ChevronDown size={15} /></button>
      </div>
      {aberto && (
        <div ref={lista} role="listbox" style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, marginTop: 2, width: Math.max(largura, 360), maxWidth: "92vw", maxHeight: 340, overflowY: "auto", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, boxShadow: "0 8px 22px rgba(0,0,0,.14)" }}>
          {visiveis.map((o, i) => {
            const cab = o.grupo && o.grupo !== grupoAnterior ? o.grupo : null;
            grupoAnterior = o.grupo || grupoAnterior;
            return (
              <React.Fragment key={`${o.valor}-${i}`}>
                {cab && <div style={{ padding: "6px 10px 3px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: C.soft, background: "#fafbfc" }}>{cab}</div>}
                <div data-i={i} role="option" aria-selected={o.valor === valor} onMouseDown={(e) => { e.preventDefault(); escolher(o); }} onMouseEnter={() => setAtivo(i)}
                     style={{ padding: "7px 10px", cursor: "pointer", background: i === ativo ? C.tealSoft : o.valor === valor ? "#f4fbfa" : "#fff", borderBottom: `1px solid #f1f3f4` }}>
                  <div style={{ fontSize: 12.5, color: C.ink, fontWeight: o.valor === valor ? 700 : 600 }}>{o.rotulo}</div>
                  {o.detalhe && <div style={{ fontSize: 11.5, color: C.soft }}>{o.detalhe}</div>}
                </div>
              </React.Fragment>
            );
          })}
          {buscando && <div style={{ padding: 8, fontSize: 12, color: C.soft }}>Buscando no cadastro…</div>}
          {!buscando && visiveis.length === 0 && <div style={{ padding: 8, fontSize: 12, color: C.soft }}>{vazio}</div>}
          {buscarRemoto && !buscando && q.trim().length > 0 && q.trim().length < 3 && <div style={{ padding: "6px 10px", fontSize: 11, color: C.soft }}>Com 3 letras, busca também em todo o cadastro.</div>}
        </div>
      )}
    </div>
  );
}
